/**
 * The state of the one call this app can be on, and everything that changes it.
 *
 * Lives above the router rather than inside a screen, because a call outlives
 * navigation: answering on the chat list, walking into a different chat and
 * coming back has to leave the call untouched. The overlay it drives is rendered
 * at the top of the tree for the same reason.
 *
 * The phase machine here mirrors the server's in calls.ts but is not a copy of
 * it — the server tracks who is allowed to do what, this tracks what the person
 * looking at the screen should see. Where they disagree the server wins: every
 * transition below is either a local intent that is about to be sent, or a
 * reaction to an event that has already been decided.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api/client.ts';
import { nativePlatform } from '../api/origin.ts';
import { chatSocket, type ServerEvent } from '../api/socket.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { useChat } from '../chat/ChatContext.tsx';
import {
  setCallRinging,
  setCallSpeaker,
  startCallAudio,
  stopCallAudio,
  watchCallAudio,
} from '../lib/callAudio.ts';
import {
  drainCallKitEvents,
  drainNativePushCallActions,
  hasNativeCallKit,
  hasNativePushCallActions,
  isFreshAnswer,
  reduceCallKitEvents,
  reportCallConnected,
  reportCallEnded,
  reportCallMuted,
  reportIncomingCall,
  reportOutgoingCall,
  watchCallKitActions,
  type CallKitActions,
  type CallKitEvent,
} from '../lib/callKit.ts';
import {
  describeCall,
  localMediaNote,
  silentSenderNote,
  type CallNote,
  type LocalTrackFacts,
} from '../lib/callDiagnostics.ts';
import { ringtoneContext, startRingtone } from '../lib/ringtone.ts';
import { cameraFacing, flipCamera, type Facing } from '../lib/capture.ts';
import { reportClientError } from '../lib/errorReporter.ts';
import {
  closeOrphanedNativeCall,
  hasNativeCallPeer,
  NativeCallPeer,
} from '../lib/nativeCallPeer.ts';
import { CallStatsReporter } from '../lib/callTelemetry.ts';
import { helloDetail } from '../lib/callStats.ts';
import {
  callMediaError,
  callTuning,
  openCallMedia,
  PeerCall,
  type CallConnection,
  type IceConfig,
  type PeerHandlers,
  type SignalPayload,
} from '../lib/webrtc.ts';
import { RemoteAudio } from './RemoteAudio.tsx';

/**
 * Where a call is. Mirrors the handoff's `phase`, with 'ringing' split out for
 * the caller's side of what the design calls the outgoing screen.
 */
export type CallPhase =
  | 'outgoing'
  | 'incoming'
  | 'active'
  | 'busy'
  | 'lost'
  | 'permission'
  | 'ended';

export interface CallState {
  id: string;
  conversationId: string;
  /** Who is on the other end, for the identity block. */
  peerId: string;
  peerName: string;
  /** Their avatar, so the ringing card is a face rather than a letter. */
  peerAvatarId: string | null;
  video: boolean;
  direction: 'outgoing' | 'incoming';
  /**
   * The UUID the OS knows this call by, or null off the phone. Incoming it is
   * the server's call id — already a UUID, which is what makes a socket
   * report and a push report of the same ring collapse into one. Outgoing it
   * is minted locally, because the invite only learns its server id at
   * `call:accepted` and the system UI has to exist from the moment of the tap.
   */
  nativeCallId: string | null;
  phase: CallPhase;
  muted: boolean;
  cameraOff: boolean;
  speakerOn: boolean;
  facing: Facing;
  /** Ticks once a second from the moment the call was answered, never before. */
  elapsedSeconds: number;
  /**
   * Answered, and no media path yet. True from the answer to the first
   * `connected`; the overlay spends it on lib/callConnecting.ts's status
   * line, because with «сначала через релей» on the gap is seconds long and
   * an unmoving 0:00 reads as a hang.
   */
  connecting: boolean;
  /** A blip in the media path: the call continues and the banner shows. */
  reconnecting: boolean;
  /** The far end turned their camera off, or answered without video. */
  peerCameraOff: boolean;
  /** Why it finished, for the transitional «Звонок завершён» / «Отклонён». */
  endReason: 'hangup' | 'declined' | 'busy' | 'timeout' | 'failed' | 'cancelled' | null;
  /** Filled in on 'permission' and 'lost', where the screen explains itself. */
  message: string | null;
  /**
   * Whether the system declined to ring for this call.
   *
   * On iOS the incoming screen is CallKit's, not this app's — reporting a call
   * and then drawing our own ringing screen over the system's produced two
   * call UIs on top of each other, which is what a phone must never look like.
   * So the overlay stays out of the incoming phase entirely... unless the
   * system refused the ring (Do Not Disturb and its relatives, error 3), and
   * then there is no system UI to defer to and the page takes the ring back —
   * screen and ringtone both. False everywhere CallKit does not exist.
   */
  ringRefused: boolean;
}

interface CallContextValue {
  call: CallState | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  /** True once the call screen has been collapsed to the pill at the top. */
  minimized: boolean;
  /** Whether a relay is deployed; false means calls can fail to connect. */
  hasRelay: boolean;
  /**
   * True when calls on this device are carried by the shell's own WebRTC —
   * the overlay then renders no `<video>`, because there is never a stream
   * to bind. Constant for the life of the app, exposed here so the overlay
   * does not import the flag from two places.
   */
  nativeCall: boolean;
  /**
   * True only when the shell has confirmed it draws this call's video —
   * `create`'s own answer, per call, false between calls. The overlay's
   * transparency is gated on this and nothing weaker: a page that went
   * see-through over a shell with nothing underneath showed the chat list
   * through a live call (веха B, first device pass).
   */
  nativeCallVideo: boolean;
  /**
   * True while the system is the one ringing for an incoming call, and so the
   * one drawing its screen. The overlay renders nothing then: two call UIs at
   * once is what this flag exists to prevent. See CallState.ringRefused for
   * the case where the system declines and the page takes the ring back.
   */
  systemRinging: boolean;
  placeCall: (
    conversationId: string,
    peerId: string,
    peerName: string,
    peerAvatarId: string | null,
    video: boolean,
  ) => void;
  /**
   * Calls back from a call record in the thread, which only knows which
   * conversation it is in. The peer is looked up here so the bubble does not
   * have to be handed one.
   */
  callBack: (conversationId: string, video: boolean) => void;
  answer: (video: boolean) => void;
  decline: () => void;
  hangUp: () => void;
  toggleMute: () => void;
  toggleCamera: () => void;
  toggleSpeaker: () => void;
  flip: () => void;
  setMinimized: (value: boolean) => void;
  /** «Повторить» on the failed screen: same peer, same kind. */
  retry: () => void;
  dismiss: () => void;
  /**
   * Files a diagnostic against the call that is happening.
   *
   * Exposed because the overlay owns the `<video>` elements and therefore is
   * the only place that can notice one of them holding a live stream and
   * painting nothing — while this file is the only one that knows which call
   * that was, which is what makes such a row readable a week later.
   */
  noteCall: (note: CallNote) => void;
}

const CallCtx = createContext<CallContextValue | null>(null);

export function useCall(): CallContextValue {
  const value = useContext(CallCtx);
  if (!value) throw new Error('useCall must be used inside CallProvider');
  return value;
}

/** How long the «Звонок завершён» card stays before the overlay lets go. */
const ENDED_LINGER_MS = 1400;

/**
 * How many notes an outgoing call may bank before it has a timeline to put them
 * on. Two or three is the real number — the route line and the codec one — and
 * the cap is only here so that a call which never connects cannot grow a list.
 */
const PENDING_NOTES_LIMIT = 32;

/**
 * When capture is asked again what it turned into.
 *
 * Two readings after the first, not one, and the late one is 25 seconds because
 * that is what the first device pass measured: both an iPhone and an iPad
 * opened their camera and microphone suspended and took twenty to twenty-five
 * seconds to start producing. A single five-second reading would have called
 * both of those calls dead and stopped there.
 */
const LOCAL_MEDIA_READINGS: readonly { at: string; afterMs: number }[] = [
  { at: 'settled', afterMs: 5000 },
  { at: 'late', afterMs: 25_000 },
];

/** How the answer that opened this device's microphone arrived. See localMediaNote. */
type AnswerOrigin = 'outgoing' | 'page' | 'callkit';

export function CallProvider({ children }: { children: ReactNode }): React.ReactElement {
  const { user } = useAuth();
  // Only for resolving a conversation back to its peer — see callBack.
  const { chats } = useChat();
  const [call, setCall] = useState<CallState | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [hasRelay, setHasRelay] = useState(true);
  /** Whether the shell draws the current call's video; see nativeCallVideo. */
  const [nativeVideo, setNativeVideo] = useState(false);

  /**
   * The live connection, and the call it belongs to.
   *
   * Refs rather than state because the socket handler below is registered once
   * and must always see the current call — re-subscribing on every state change
   * would drop events in the gap. The same arrangement ChatContext uses for
   * activeChatIdRef.
   */
  const peer = useRef<CallConnection | null>(null);
  const callRef = useRef<CallState | null>(null);
  const iceRef = useRef<IceConfig | null>(null);
  /**
   * The credentials *this* call will use, held as the promise of them.
   *
   * A promise rather than the config, and one per call rather than one per
   * session — the same shape mediaRef has, for a related reason. Both ends of a
   * call ask for this: an outgoing one when it is placed, and again when the
   * invite is accepted, and a call answered quickly enough arrives at the
   * second before the first has finished.
   *
   * Per call is the part that matters. Read out of a plain cache, as this used
   * to be, an outgoing call reused whatever credential the device had fetched
   * first — which meant one TURN username for every call of a session, and
   * coturn counts allocations per username: two per side per call, so the
   * seventh call was answered with «486 out of ports» while the relay sat
   * nearly empty. It also meant a tab left open past the hour offered with a
   * credential that had expired, and settings from the admin page that were
   * one call stale.
   */
  const icePromise = useRef<Promise<IceConfig> | null>(null);
  const timerRef = useRef<number | null>(null);
  const lingerRef = useRef<number | null>(null);
  /** When this call opened, so a report can say how long it was given. */
  const openedAtRef = useRef(0);
  /**
   * The camera and microphone this call is using, held as the promise of them.
   *
   * A promise rather than the stream, because the two places that want it can
   * both come first: an outgoing call opens the camera the moment it is placed,
   * to show the preview, and opens it again when the invite is accepted, to hand
   * it to the connection — and a call answered quickly enough arrives at the
   * second before the first has finished. Keeping the promise is what makes
   * that one acquisition instead of two on the same device, which some phones
   * refuse outright with NotReadableError and the rest pay for with a second
   * camera warm-up while the preview is already on screen.
   */
  const mediaRef = useRef<Promise<MediaStream> | null>(null);
  /**
   * The native connection, held as the promise of it — mediaRef's shape, for
   * mediaRef's reason. On an outgoing call `create` starts at the tap (it is
   * what opens the native capture, so it is also the earliest moment to learn
   * a permission was refused) and the same promise is awaited again when the
   * invite is accepted; a call answered quickly enough arrives at the second
   * before the first has resolved, and keeping the promise makes that one
   * connection instead of two.
   */
  const nativePeerRef = useRef<Promise<NativeCallPeer> | null>(null);
  /**
   * Which call the connection being built belongs to.
   *
   * Building one is asynchronous on both paths — a permission prompt, a
   * capture opening, a bridge round trip — and a call can end inside that
   * window: the far side hangs up on a ring somebody just answered. What used
   * to happen then is that `teardown` ran first and the finished connection
   * was stored afterwards, so the camera light stayed on for a call that no
   * longer existed, and the closing row said «answered but never connected ·
   * no connection» about a call whose connection arrived a moment too late.
   * Bumped by teardown; compared after every await that could outlive a call.
   */
  const buildToken = useRef(0);
  /**
   * Which of the three ways this device came to open its capture, and the timer
   * for the second look at what that capture produced. Both are only ever read
   * by the local-media census; see noteLocalMedia.
   */
  const mediaOrigin = useRef<AnswerOrigin>('outgoing');
  const settleTimers = useRef<number[]>([]);
  /** Whether this call has already taken the audio session; see placeCall. */
  const audioClaimed = useRef(false);
  /**
   * Notes filed before there was a reporter to file them on.
   *
   * The caller claims the audio session at the tap and the plugin says which
   * speaker it settled on straight away — both a good half-minute before
   * `call:accepted` builds the reporter. Without this the timeline of an
   * outgoing call simply began after the answer, and the one line saying where
   * its sound was coming out of was not on it. The same shape as
   * `pendingSignals`, and for the same reason: the thing that will hold it does
   * not exist yet, and the fact does.
   */
  const pendingNotes = useRef<{ detail: string; at: number }[]>([]);
  /**
   * Whether the media path ever came up. The difference between a call that
   * ended and a call that never happened is invisible everywhere else: both
   * leave the same row in the thread and both look like a hang-up from here.
   */
  const connectedRef = useRef(false);
  /**
   * Signalling that arrived before there was a connection to give it to.
   *
   * The gap is real: the callee sends `call:accept` and only then opens the
   * microphone, and on a phone that is a permission prompt long — while the
   * caller answers the same event by making its offer immediately. An offer
   * dropped in that window is a call that rings, is answered, and then sits in
   * silence forever, which is the worst shape a failure can take here.
   */
  const pendingSignals = useRef<SignalPayload[]>([]);
  /** Kept so «Повторить» knows who to call back without the call still existing. */
  const lastAttempt = useRef<{
    conversationId: string;
    peerId: string;
    peerName: string;
    peerAvatarId: string | null;
    video: boolean;
  } | null>(null);
  /**
   * The cold-start join: an answer tapped on the lock screen before any call
   * existed here, held by call id until the matching `call:incoming` arrives
   * from deliverPending over the freshly-connected socket. Checked for
   * freshness when it is applied, not when it is held — the queue it was
   * drained from survives the app being dead for days.
   */
  const pendingAnswer = useRef<{ callId: string; at: number } | null>(null);
  /**
   * Native call ids whose end has already been told to CallKit, so the safety
   * net in `finish` does not re-report what decline/hangUp/the socket handler
   * already reported with a more precise reason. One entry per call, ever.
   */
  const reportedEnds = useRef(new Set<string>());
  /**
   * What this call is telling the server about itself, and the clock that makes
   * it do so.
   *
   * One per call, made where the connection is made and finished where the
   * connection is closed — the last reading has to be taken before the peer
   * connection goes away, which is the same rule the closing diagnostic line
   * has always lived by. Null between calls, which is what makes every method
   * on it safe to reach for with `?.` from a handler that may fire either way.
   */
  const reporterRef = useRef<CallStatsReporter | null>(null);
  const statsTimer = useRef<number | null>(null);

  /** One line in this call's log, when there is a call to log it against. */
  const noteEvent = useCallback((kind: string, detail: string) => {
    reporterRef.current?.event(kind, detail, Date.now());
  }, []);

  /** Tells the system UI this call is over, at most once per call. */
  const reportEndOnce = useCallback((nativeCallId: string | null, reason: string) => {
    if (!nativeCallId || reportedEnds.current.has(nativeCallId)) return;
    reportedEnds.current.add(nativeCallId);
    reportCallEnded(nativeCallId, reason);
  }, []);

  const update = useCallback((change: Partial<CallState>) => {
    setCall((current) => {
      if (!current) return current;
      const next = { ...current, ...change };
      callRef.current = next;
      return next;
    });
  }, []);

  /**
   * Files one note about the call that is happening, with the call attached.
   *
   * Every diagnostic in lib/webrtc.ts and every failure below comes through
   * here, because none of them mean anything on their own: «connection failed»
   * is unreadable a week later, and «out · video · relay=yes · phase=active ·
   * 9s · route none» is a diagnosis. Which person it was is the row's user_id,
   * and which browser is its user_agent, both already stored.
   */
  const noteCall = useCallback((note: CallNote) => {
    const current = callRef.current;
    const facts = describeCall({
      direction: current?.direction ?? 'outgoing',
      video: current?.video ?? false,
      relayConfigured: iceRef.current?.hasRelay ?? null,
      phase: current?.phase ?? 'none',
      ageMs: openedAtRef.current === 0 ? 0 : Date.now() - openedAtRef.current,
      native: hasNativeCallPeer,
      shell: nativePlatform(),
    });
    reportClientError({ kind: 'call', detail: note.detail, message: `${note.message} · ${facts}` });
    // The same note, on the call's own timeline. client_errors is throttled per
    // detail and holds only what went wrong, which is right for it and wrong
    // here: the chart is read minute by minute, and «restarted at 0:41» is most
    // of what makes it legible.
    const line = `${note.detail} · ${note.message}`;
    const at = Date.now();
    if (reporterRef.current === null) {
      if (pendingNotes.current.length < PENDING_NOTES_LIMIT) {
        pendingNotes.current.push({ detail: line, at });
      }
      return;
    }
    reporterRef.current.event('note', line, at);
  }, []);

  /**
   * A call that could not be set up at all: no camera, no microphone, no ICE
   * credentials, or an SDP the other side would not take.
   *
   * The name of the fault is the detail rather than the step it happened in,
   * because that is what separates the answers: `NotAllowedError` is a person
   * who said no to a permission prompt and needs no investigation, while
   * `InvalidStateError` in the same place is this app's bug.
   */
  const noteSetupFailure = useCallback(
    (step: string, error: unknown) => {
      const name =
        error instanceof DOMException || error instanceof Error ? error.name : 'unknown';
      noteCall({
        detail: `call/setup/${name}`,
        message: `${step} · ${name}: ${error instanceof Error ? error.message : String(error)}`,
      });
    },
    [noteCall],
  );

  /**
   * Whether the way this call ended is a fault, and the row if it is.
   *
   * Two endings are, and no others. A call that reached 'active' — both people
   * agreed to speak — and never got its media path up is the failure this whole
   * feature has to be able to see, and it usually ends as an ordinary hang-up by
   * whoever got tired of the silence, so nothing else would ever mark it. And
   * anything the server or the connection called 'failed' is one by definition.
   * A call that was declined, timed out, hit a busy line or simply ended is the
   * app working, and filing those would bury the two that are not.
   *
   * Called before teardown, which is what makes the statistics readable: the
   * getStats() call inside `describe` is issued synchronously here, before the
   * peer connection is closed out from under it.
   */
  const reportOutcome = useCallback(
    (phase: CallPhase, reason: CallState['endReason']) => {
      const current = callRef.current;
      if (!current) return;

      const failed = reason === 'failed' || phase === 'lost';
      const silent = current.phase === 'active' && !connectedRef.current;
      const connection = peer.current;

      if (!failed && !silent) {
        // A call that worked. Whether it worked *well* is a question nothing
        // else in this app asks, and the person who had it cannot answer it in
        // bytes — «плохо видно» is an opinion. This is the only place the
        // encoder can still be asked, and it stays quiet unless the answer is a
        // finding; see qualityNote.
        void connection?.quality().then((note) => {
          if (note) noteCall(note);
        });
        return;
      }

      const description = connection?.describe() ?? Promise.resolve('no connection');
      void description.then((line) => {
        noteCall({
          detail: failed ? 'call/ended-failed' : 'call/never-connected',
          message: `${failed ? `ended as ${reason ?? 'failed'}` : 'answered but never connected'} · ${line}`,
        });
      });
    },
    [noteCall],
  );

  /**
   * What this device's own camera and microphone turned out to be doing.
   *
   * The one thing about a call nothing here was reading, and the one that a
   * call whose far end sees black and hears nothing depends on: `getUserMedia`
   * resolving is not the same as capture running, and on iOS the difference is
   * whether the app was in front when it was asked. localMediaNote holds the
   * rules and stays quiet when there is nothing wrong.
   */
  const noteLocalMedia = useCallback(
    async (stream: MediaStream, at: string) => {
      const tracks: LocalTrackFacts[] = stream.getTracks().map((track) => {
        // Only the camera's settings are worth reading: a microphone has no
        // equivalent of «live but producing no frames», which is the state
        // being looked for.
        const settings = track.kind === 'video' ? track.getSettings() : null;
        return {
          kind: track.kind,
          readyState: track.readyState,
          muted: track.muted,
          enabled: track.enabled,
          width: settings?.width,
          height: settings?.height,
          frameRate: settings?.frameRate,
        };
      });
      const facts = {
        origin: mediaOrigin.current,
        visibility: document.visibilityState,
        at,
      };
      const note = localMediaNote(tracks, facts);
      if (note) {
        noteCall(note);
        return;
      }
      // Tracks that read healthy are not the end of the question, and on the
      // late reading they are the interesting half of it: the failure both
      // device passes ended in has every track live and unmuted with nothing
      // coming out of the encoder. Asked only there because before the capture
      // has settled a zero is the ordinary state of a call that is starting.
      if (at !== 'late') return;
      const silent = silentSenderNote(tracks, facts, (await peer.current?.audioBytesSent()) ?? null);
      if (silent) noteCall(silent);
    },
    [noteCall],
  );

  /**
   * The native path's version of the census above. There are no page tracks to
   * read — the capture belongs to the shell — but the question the census was
   * really asking survives the move: bytes on the wire, read at the same
   * moments. A measured zero on the late reading files the same
   * `call/local/silent-sender/late` slug silentSenderNote does, deliberately:
   * that slug is the go/no-go metric for this whole path, and it has to mean
   * one thing across both implementations. Null is «could not ask», not a
   * finding — the same rule as everywhere else.
   */
  const noteNativeMedia = useCallback(
    async (at: string) => {
      const bytes = (await peer.current?.audioBytesSent()) ?? null;
      if (at !== 'late' || bytes !== 0) return;
      noteCall({
        detail: 'call/local/silent-sender/late',
        message: `native sender sent 0B · at ${at} · answer ${mediaOrigin.current} · page ${document.visibilityState}`,
      });
    },
    [noteCall],
  );

  /**
   * The camera and microphone for this call, opened at most once.
   *
   * `video` is only consulted on the first call, which is safe because the only
   * place that asks twice is an outgoing call — and there the preview and the
   * connection are the same kind of call by definition. The callee asks once,
   * after it knows whether «Ответить без видео» was chosen.
   */
  const openMedia = useCallback((video: boolean): Promise<MediaStream> => {
    // Whatever tuning this device has already fetched. An outgoing call opens
    // the camera at the tap, which can be before the first /ice answer of the
    // session has landed — PeerCall applies the numbers again once it has them,
    // so the capture ends up tuned either way and the preview never waits on a
    // round trip.
    mediaRef.current ??= openCallMedia(video, cameraFacing(), callTuning(iceRef.current));
    return mediaRef.current;
  }, []);

  /** Everything that has to stop when a call is over, whatever ended it. */
  const teardown = useCallback(() => {
    // Before the connection is closed, and that ordering is the whole of it:
    // getStats on a closed peer connection answers nothing, so the last few
    // seconds of a call — the restart that did not help, the freeze it ended
    // on — exist only if they are read here. `finish` issues the read
    // synchronously and posts what it gets, so nothing is awaited on the way
    // out of a call.
    if (statsTimer.current !== null) {
      window.clearInterval(statsTimer.current);
      statsTimer.current = null;
    }
    const reporter = reporterRef.current;
    reporterRef.current = null;
    if (reporter) void reporter.finish(Date.now());
    peer.current?.close();
    peer.current = null;
    // Anything still being built belongs to a call that is over; see buildToken.
    buildToken.current += 1;
    // Handing the audio session back is what lets whatever was playing before
    // the call resume. Unconditional, like the track stops below: a call that
    // never opened its microphone never took the session, and giving back one
    // that was never taken is nothing.
    stopCallAudio();
    audioClaimed.current = false;
    connectedRef.current = false;
    pendingSignals.current = [];
    // A call that never reached a reporter — cancelled while ringing, refused
    // for a permission — leaves its notes in client_errors and nowhere else,
    // which is right: there is no timeline to put them on, and carrying them
    // into the next call would date-stamp them into somebody else's.
    pendingNotes.current = [];
    // The connection stops these when it has one, but it does not always get
    // one: a call cancelled while it was still ringing, or refused for a
    // permission the person then granted, would otherwise leave the camera
    // light on with nothing on screen to explain it. Stopping a stopped track
    // is nothing, so this is safe on top of PeerCall.close.
    const media = mediaRef.current;
    mediaRef.current = null;
    void media
      ?.then((stream) => {
        for (const track of stream.getTracks()) track.stop();
      })
      // Media that never opened is what the report in the catch block was about.
      .catch(() => {});
    // Resolve-then-close, for the same race in native clothes: a call that
    // ended while its connection was still being created has nobody else to
    // close it. Idempotent on top of the peer.current.close() above — by the
    // time this resolves they are usually the same object, closed twice safely.
    // The next call fetches its own credentials; see icePromise.
    icePromise.current = null;
    const native = nativePeerRef.current;
    nativePeerRef.current = null;
    void native?.then((connection) => connection.close()).catch(() => {});
    setNativeVideo(false);
    for (const timer of settleTimers.current) window.clearTimeout(timer);
    settleTimers.current = [];
    setLocalStream(null);
    setRemoteStream(null);
    setMinimized(false);
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearCall = useCallback(() => {
    // Ordinarily a no-op — every ending reports before it gets here — but a
    // sign-out mid-call reaches this directly, and a CallKit screen nobody
    // ends rings forever.
    reportEndOnce(callRef.current?.nativeCallId ?? null, 'failed');
    teardown();
    callRef.current = null;
    setCall(null);
  }, [reportEndOnce, teardown]);

  /**
   * Shows the ended card, then lets go. The linger is what the handoff asks for
   * — a call that vanishes the instant it ends reads as a crash.
   */
  const finish = useCallback(
    (phase: CallPhase, endReason: CallState['endReason'], message?: string) => {
      // Before teardown: it is the last moment the connection can still be
      // asked what happened to it.
      noteEvent('end', `${phase}${endReason ? ` · ${endReason}` : ''}`);
      reportOutcome(phase, endReason);
      // The net behind the site-specific reports: decline, hangUp and the
      // socket handler have already said something more precise, and this
      // catches every path that did not — a permission refusal, a busy line —
      // because a CallKit screen nobody ends rings forever.
      reportEndOnce(callRef.current?.nativeCallId ?? null, endReason ?? 'failed');
      teardown();
      const current = callRef.current;
      if (!current) return;
      const next: CallState = { ...current, phase, endReason, message: message ?? null };
      callRef.current = next;
      setCall(next);

      if (lingerRef.current !== null) window.clearTimeout(lingerRef.current);
      // 'lost' and 'permission' have buttons on them and wait to be dismissed;
      // a plain ending does not.
      if (phase === 'ended') {
        lingerRef.current = window.setTimeout(clearCall, ENDED_LINGER_MS);
      }
    },
    [clearCall, noteEvent, reportEndOnce, reportOutcome, teardown],
  );

  /**
   * A page being closed mid-call.
   *
   * Nothing else here runs then — teardown belongs to a call *ending*, and this
   * is a call that simply stops existing — so without this the last batch, and
   * with it every sample since the previous flush, goes with the page. `pagehide`
   * rather than `beforeunload` because it is the one Safari fires on iOS, which
   * is where calls are most often ended by walking away from them.
   */
  useEffect(() => {
    const handler = () => {
      const reporter = reporterRef.current;
      reporterRef.current = null;
      if (reporter) void reporter.finish(Date.now());
    };
    window.addEventListener('pagehide', handler);
    return () => window.removeEventListener('pagehide', handler);
  }, []);

  /** The elapsed timer starts when the call connects, never when it opens. */
  const startTimer = useCallback(() => {
    if (timerRef.current !== null) return;
    timerRef.current = window.setInterval(() => {
      setCall((current) => {
        if (!current) return current;
        const next = { ...current, elapsedSeconds: current.elapsedSeconds + 1 };
        callRef.current = next;
        return next;
      });
    }, 1000);
  }, []);

  /**
   * Fetched per call rather than once at sign-in: a TURN credential is
   * deliberately short-lived, and a tab left open overnight would otherwise
   * place its first morning call with an expired one.
   */
  const loadIce = useCallback(async (): Promise<IceConfig> => {
    try {
      const config = await api.iceConfig();
      iceRef.current = config;
      setHasRelay(config.hasRelay);
      return config;
    } catch (error) {
      // api/client.ts has already filed the HTTP failure, but not as something
      // that happened to a call — and a TURN credential that could not be
      // fetched is a call that will only ever work on one network, which is a
      // different sentence from «a GET returned 500».
      noteSetupFailure('ice config', error);
      throw error;
    }
  }, [noteSetupFailure]);

  /**
   * Fresh credentials for the call that is starting, or the ones this device
   * already had if the fetch fails — a call over a stale credential still
   * connects on the paths that need no relay, and refusing to place it would
   * be worse than the ICE errors it might file.
   */
  const iceForCall = useCallback((): Promise<IceConfig> => {
    icePromise.current ??= loadIce().catch((error: unknown) => {
      const cached = iceRef.current;
      if (cached) return cached;
      throw error;
    });
    return icePromise.current;
  }, [loadIce]);

  /** The handlers both implementations of CallConnection are built over. */
  const peerHandlers = useCallback(
    (onSignal: (payload: SignalPayload) => void): PeerHandlers => ({
      onSignal,
      onRemoteStream: (incoming) => setRemoteStream(incoming),
      onRemoteCamera: (on) => update({ peerCameraOff: !on }),
      onDiagnostic: noteCall,
      onConnectionChange: (state) => {
        noteEvent('connection', state);
        if (state === 'connected') {
          connectedRef.current = true;
          update({ connecting: false, reconnecting: false });
          startTimer();
          /**
           * The system rang; the app takes it from here.
           *
           * CallKit owns the screen for as long as it knows about the call,
           * and on a phone that is not in front — answered from the lock
           * screen, or simply put down — that screen is the *system's* call
           * UI, with this app's own one alive underneath it. Two call screens
           * for one call is what a phone must never look like, so the moment
           * the media path is up the system's copy is retired: the ring has
           * already done its job, and everything after it — the picture, the
           * controls, the timer — is this app's.
           *
           * What it costs is what CallKit was giving for free after the
           * answer: lock-screen controls and an accurate duration in Recents.
           * What it must *not* cost is the audio, which CallKit deactivates
           * on its way out — see callKitDidDeactivate, which keeps the unit
           * running when a native call is still live.
           */
          const current = callRef.current;
          if (current?.nativeCallId) reportEndOnce(current.nativeCallId, 'handoff');
        }
        // 'disconnected' is usually a blip that ICE recovers from on its own,
        // so it shows a banner rather than ending anything.
        if (state === 'disconnected') update({ reconnecting: true });
        if (state === 'failed') update({ reconnecting: true });
      },
    }),
    [noteCall, noteEvent, reportEndOnce, startTimer, update],
  );

  /**
   * The native connection, created at most once per call — see nativePeerRef
   * for why it is the promise that is kept.
   */
  const openNativePeer = useCallback(
    (config: IceConfig, video: boolean): Promise<NativeCallPeer> => {
      nativePeerRef.current ??= NativeCallPeer.create(
        config,
        // The role decides the ice policy, and the policy is fixed at
        // creation — which is why it travels here rather than with start().
        { video, facing: cameraFacing(), caller: callRef.current?.direction === 'outgoing' },
        peerHandlers((payload) => {
          // Read at send time rather than captured, because on an outgoing
          // call the connection exists before the server has named the call.
          // Nothing signals in that window — the caller's offer waits for
          // start(), which waits for call:accepted — so an empty id here is a
          // signal for a call that is already gone, and dropping it is right.
          const id = callRef.current?.id;
          if (id) chatSocket.signal(id, payload);
        }),
      ).then((connection) => {
        // Only now may the overlay consider transparency: the shell has
        // answered whether there is a picture underneath at all.
        setNativeVideo(connection.rendersVideo);
        return connection;
      });
      return nativePeerRef.current;
    },
    [peerHandlers],
  );

  /**
   * Starts reporting this call, from the moment it has a name and a connection
   * to read.
   *
   * The interval is the server's — the same answer the bitrates come in — so
   * how often devices report is a knob rather than a constant, and a
   * deployment watching a bad week can turn it up without a release.
   *
   * `read` looks the connection up at each tick rather than closing over it:
   * the native path resolves its connection after this runs, and a reading
   * taken before there is one is a row of nulls, which is the honest answer to
   * «what was it doing» in the first seconds of a call.
   */
  const startReporting = useCallback(
    (config: IceConfig, callId: string, wantVideo: boolean) => {
      const tuning = callTuning(config);
      const reporter = new CallStatsReporter({
        read: () => peer.current?.stats() ?? Promise.resolve([]),
        post: (batch) => api.reportCallTelemetry(callId, batch).then(() => undefined),
      });
      reporterRef.current = reporter;
      reporter.event(
        'hello',
        helloDetail({
          native: hasNativeCallPeer,
          shell: nativePlatform(),
          video: wantVideo,
          direction: callRef.current?.direction ?? 'outgoing',
          hasRelay: config.hasRelay,
          relayFirst: tuning.callRelayFirst,
          width: tuning.callWidth,
          height: tuning.callHeight,
          fps: tuning.callFps,
          videoKbps: tuning.callVideoKbps,
          audioKbps: tuning.callAudioKbps,
          degradation: tuning.callDegradation,
          codec: tuning.callCodec,
        }),
        Date.now(),
      );
      // Whatever was filed before this existed, in the order it happened and
      // after the hello that introduces the side which filed it.
      for (const note of pendingNotes.current) reporter.event('note', note.detail, note.at);
      pendingNotes.current = [];
      if (statsTimer.current !== null) window.clearInterval(statsTimer.current);
      statsTimer.current = window.setInterval(
        () => void reporter.tick(Date.now()),
        Math.max(2, tuning.callStatsSeconds) * 1000,
      );
    },
    [],
  );

  /** Builds the peer connection both roles use; only the caller then starts it. */
  const buildPeer = useCallback(
    async (
      config: IceConfig,
      callId: string,
      wantVideo: boolean,
    ): Promise<CallConnection | null> => {
      const token = buildToken.current;
      /** Whether the call this is being built for is still the current one. */
      const stale = () => token !== buildToken.current;
      // Here rather than in either branch: this is the one place a connection
      // is made, on both paths, and the one place the call already has a name
      // to report under.
      startReporting(config, callId, wantVideo);
      if (hasNativeCallPeer) {
        // No getUserMedia anywhere on this path, and that is the whole point:
        // a WebKit capture under a CallKit session activation is the measured
        // 0-byte microphone this plugin exists to end. The shell owns capture
        // and rendering; localStream stays null and the overlay knows not to
        // ask for it.
        const connection = await openNativePeer(config, wantVideo);
        if (stale()) {
          // The call ended while the shell was opening its capture. Closing
          // rather than storing is the whole point: a connection nobody holds
          // is a camera light with no call behind it.
          connection.close();
          return null;
        }
        peer.current = connection;
        // The session claim keeps its meaning — CallAudioPlugin still owns
        // category, route and recovery — it just no longer races a WebKit
        // capture to it.
        if (!audioClaimed.current) {
          startCallAudio(wantVideo);
          audioClaimed.current = true;
        }

        // The same two readings the web path takes, asked of the only witness
        // this path has — the sender's byte count. See noteNativeMedia.
        for (const reading of LOCAL_MEDIA_READINGS) {
          settleTimers.current.push(
            window.setTimeout(() => void noteNativeMedia(reading.at), reading.afterMs),
          );
        }

        const queued = pendingSignals.current;
        pendingSignals.current = [];
        for (const payload of queued) {
          await connection.accept(payload).catch((error: unknown) => {
            noteSetupFailure('queued signal', error);
          });
        }
        return connection;
      }

      const stream = await openMedia(wantVideo);
      // The same window on the web path: teardown already stopped whatever
      // this stream holds, and building a connection over it would revive
      // nothing but the indicator light.
      if (stale()) return null;
      setLocalStream(stream);
      // After the microphone is open, never before: WebKit configures the audio
      // session itself when capture starts, so a category claimed ahead of this
      // line is quietly replaced a moment later — see startCallAudio. And never
      // twice, which is the other half of the same rule: on an outgoing call
      // capture opened at the tap and the session was claimed with it, so doing
      // it again here would be the mid-capture reconfiguration that placeCall's
      // comment describes, arriving from the other direction.
      if (!audioClaimed.current) {
        startCallAudio(wantVideo);
        audioClaimed.current = true;
      }

      // The readings that decide it. A track the system suspended looks exactly
      // like one still warming up at the instant it opens; what tells them
      // apart is what it turned into while the call was running.
      void noteLocalMedia(stream, 'open');
      for (const reading of LOCAL_MEDIA_READINGS) {
        settleTimers.current.push(
          window.setTimeout(() => void noteLocalMedia(stream, reading.at), reading.afterMs),
        );
      }

      const connection = new PeerCall(
        config,
        stream,
        peerHandlers((payload) => chatSocket.signal(callId, payload)),
        { caller: callRef.current?.direction === 'outgoing' },
      );
      peer.current = connection;

      // Anything that arrived while the camera was still opening. In order, and
      // awaited, because an offer has to be answered before the candidates that
      // follow it have anywhere to go.
      const queued = pendingSignals.current;
      pendingSignals.current = [];
      for (const payload of queued) {
        await connection.accept(payload).catch((error: unknown) => {
          noteSetupFailure('queued signal', error);
        });
      }

      return connection;
    },
    [
      noteLocalMedia,
      noteNativeMedia,
      noteSetupFailure,
      openMedia,
      openNativePeer,
      peerHandlers,
      startReporting,
    ],
  );

  const placeCall = useCallback(
    (
      conversationId: string,
      peerId: string,
      peerName: string,
      peerAvatarId: string | null,
      video: boolean,
    ) => {
      if (callRef.current) return;
      mediaOrigin.current = 'outgoing';
      lastAttempt.current = { conversationId, peerId, peerName, peerAvatarId, video };

      const pending: CallState = {
        // Replaced by the server's id as soon as the invite is acknowledged; the
        // outgoing screen has to exist before then or the tap feels dropped.
        id: '',
        conversationId,
        peerId,
        peerName,
        peerAvatarId,
        video,
        direction: 'outgoing',
        // Only with the native peer beside CallKit, never with CallKit alone.
        // A CXStartCallAction makes CallKit activate the audio session, and
        // over a WebKit capture that activation is the measured 0-byte
        // microphone — every pass, track live and unmuted throughout, fresh
        // captures dying the same way. The native audio unit is started from
        // that same activation, so the pair is safe together and only
        // together; a web-path call on iOS must stay off the system UI or the
        // 0B comes back.
        nativeCallId:
          hasNativeCallKit && hasNativeCallPeer ? crypto.randomUUID() : null,
        phase: 'outgoing',
        muted: false,
        cameraOff: false,
        speakerOn: true,
        facing: cameraFacing(),
        elapsedSeconds: 0,
        connecting: false,
        reconnecting: false,
        peerCameraOff: false,
        endReason: null,
        message: null,
        ringRefused: false,
      };
      callRef.current = pending;
      setCall(pending);
      openedAtRef.current = Date.now();
      chatSocket.invite(conversationId, video);
      // Started at the tap on both paths, so the credential is in flight while
      // the far end's phone rings rather than after it is answered — the one
      // moment of a call where a round trip is visible as silence.
      void iceForCall().catch(() => {});
      if (pending.nativeCallId) reportOutgoingCall(pending.nativeCallId, peerName, video);

      // The camera comes up straight away on a video call: the handoff's
      // outgoing screen is the local preview with the peer's card over it, and
      // it is also the earliest possible moment to find out permission is
      // denied. It is the same acquisition the connection will use — see
      // openMedia — so the picture never restarts when the call is answered.
      void (async () => {
        try {
          if (hasNativeCallPeer) {
            // The native twin of the early open below: `create` is what opens
            // the shell's capture, so starting it at the tap is both the
            // preview on the outgoing screen and the earliest moment to learn
            // a permission was refused. The connection lands in nativePeerRef,
            // where buildPeer awaits the same promise at call:accepted.
            const config = await iceForCall();
            await openNativePeer(config, video);
            startCallAudio(video);
            audioClaimed.current = true;
            return;
          }
          const stream = await openMedia(video);
          setLocalStream(stream);
          // Here rather than only in buildPeer, and this is the whole of the
          // caller's microphone bug. The session has to be claimed *as* capture
          // starts: WebKit puts AVAudioSession into playAndRecord the moment
          // getUserMedia begins, and the plugin's category, mode and route land
          // on top of that. Claiming it seconds later — which is what waiting
          // for call:accepted meant, since the caller opens its camera at the
          // tap and the answer comes when it comes — reconfigures the session
          // under a capture that is already running, and the microphone stops
          // producing while its track goes on reporting itself live. Two device
          // passes measured exactly that, on the caller only, on both devices,
          // with `aout 0B` against a callee that sent sound perfectly: the
          // callee's claim has always landed in the same breath as its capture.
          startCallAudio(video);
          audioClaimed.current = true;
        } catch (error) {
          noteSetupFailure('opening media to call', error);
          finish('permission', 'failed', callMediaError(error));
          if (callRef.current?.id) chatSocket.hangup(callRef.current.id);
        }
      })();
    },
    [finish, iceForCall, noteSetupFailure, openMedia, openNativePeer],
  );

  /**
   * `origin` is only ever read by the local-media census, and defaults to the
   * page because that is where the green button is; the two CallKit paths pass
   * their own. It is not a distinction the call itself makes — answering is
   * answering — but it is the one fact that separates «capture opened in an app
   * the person is looking at» from «capture opened behind the system's call
   * screen», which is the difference this app cannot otherwise see.
   */
  const answer = useCallback(
    (video: boolean, origin: AnswerOrigin = 'page') => {
      const current = callRef.current;
      if (!current || current.phase !== 'incoming') return;
      mediaOrigin.current = origin;
      chatSocket.accept(current.id, video);
      // Whichever way the answer came — the page's green button or CallKit's —
      // the system has to hear about it, or it goes on showing a ring for a
      // call two people are talking on. An answer CallKit itself performed is
      // a no-op on the other side of this. When it refuses, the ring is left
      // for the server's cancel push to clear a second or two later, and that
      // delay is the only thing anyone sees; the row is what names it.
      if (current.nativeCallId) {
        reportCallConnected(current.nativeCallId, (reason) =>
          noteCall({ detail: 'call/callkit/answer', message: `system ring not dismissed · ${reason}` }),
        );
      }
      update({ phase: 'active', connecting: true, cameraOff: current.video && !video });

      void (async () => {
        try {
          const config = await iceForCall();
          // The callee builds its connection and waits: the offer is the
          // caller's to make, and it is already on its way.
          await buildPeer(config, current.id, current.video && video);
        } catch (error) {
          noteSetupFailure('answering', error);
          finish('permission', 'failed', callMediaError(error));
          chatSocket.hangup(current.id);
        }
      })();
    },
    [buildPeer, finish, iceForCall, noteCall, noteSetupFailure, update],
  );

  /**
   * `origin` is the one fact a decline cannot be read without.
   *
   * From the caller's side every refusal looks the same — «Отклонён», a red
   * bubble, nothing filed — and one device was refusing calls inside a second,
   * which no thumb can do. A row per decline is a row this app would normally
   * refuse to write; it earns its place because a decline is rare, and because
   * the alternative is what happened here: the only device that could name the
   * cause was silent by design, and three builds went by on guesses.
   */
  const decline = useCallback(
    (origin: 'button' | 'system' = 'button') => {
      const current = callRef.current;
      if (!current) return;
      noteCall({
        detail: `call/declined/${origin}`,
        message: `declined on this device · phase ${current.phase}`,
      });
      chatSocket.decline(current.id);
      reportEndOnce(current.nativeCallId, 'declined');
      finish('ended', 'declined');
    },
    [finish, noteCall, reportEndOnce],
  );

  const hangUp = useCallback(() => {
    const current = callRef.current;
    if (!current) return;
    if (current.id) chatSocket.hangup(current.id);
    reportEndOnce(current.nativeCallId, 'hangup');
    finish('ended', 'hangup');
  }, [finish, reportEndOnce]);

  const toggleMute = useCallback(() => {
    const current = callRef.current;
    if (!current) return;
    const muted = !current.muted;
    noteEvent('mute', muted ? 'microphone off' : 'microphone on');
    peer.current?.setTrackEnabled('audio', !muted);
    // Told to the system as well, so the lock screen's mute button shows the
    // truth. When the toggle *is* the system's own action echoing back, the
    // value already matches over there and nothing moves.
    if (current.nativeCallId) reportCallMuted(current.nativeCallId, muted);
    update({ muted });
  }, [noteEvent, update]);

  const toggleCamera = useCallback(() => {
    const current = callRef.current;
    if (!current) return;
    const cameraOff = !current.cameraOff;
    noteEvent('camera', cameraOff ? 'camera off' : 'camera on');
    peer.current?.setTrackEnabled('video', !cameraOff);
    update({ cameraOff });
  }, [noteEvent, update]);

  /**
   * The speaker toggle means two different things, and both are honest.
   *
   * In the app it moves the audio route: `AVAudioSession` is the only thing
   * that can choose the loudspeaker over the earpiece, and the plugin owns it.
   * On the web it stays what it always was — a label — because a browser makes
   * that choice itself and offers nothing to argue with. What keeps the button
   * truthful on the phone is that the plugin reports the route back, so a pair
   * of headphones going in moves it without anybody tapping.
   */
  const toggleSpeaker = useCallback(() => {
    const current = callRef.current;
    if (!current) return;
    const speakerOn = !current.speakerOn;
    setCallSpeaker(speakerOn);
    update({ speakerOn });
  }, [update]);

  /**
   * The mirror turns at the tap and turns back if the camera would not.
   *
   * Optimistic because a flip takes a camera the best part of a second to open
   * and a button that does nothing for that long reads as a button that does
   * nothing at all. Reversible because it used to be optimistic and nothing
   * else: the journal said «flipped to environment» at the moment of the tap,
   * the mirror turned over, and a camera that refused — or, on the phones that
   * hold one camera open at a time, quietly handed back the one already
   * running — left both of them saying something that was not true, with the
   * rejection going nowhere at all. That is the whole of «нажимаю, и ничего»,
   * and this is the half of the fix the page owns; see PeerCall.flipCamera for
   * the half that reopens the camera.
   */
  const flip = useCallback(() => {
    const from = callRef.current?.facing ?? cameraFacing();
    const next = flipCamera();
    noteEvent('camera', `flip asked · ${from} → ${next}`);
    update({ facing: next });
    // Null is the native connection saying the picture is not the page's to
    // rebind — the overlay's mirror follows `facing`, and there is no element.
    void peer.current
      ?.flipCamera(next)
      .then((stream) => {
        if (stream) setLocalStream(stream);
      })
      .catch((error: unknown) => {
        // Both halves go back, and the remembered facing most of all: it
        // outlives the call, so a flip that did not happen would otherwise open
        // the wrong camera on the next one.
        const back = flipCamera();
        noteEvent(
          'camera',
          `flip did not happen · still ${back} · ${error instanceof Error ? error.message : 'unknown'}`,
        );
        update({ facing: back });
      });
  }, [noteEvent, update]);

  const callBack = useCallback(
    (conversationId: string, video: boolean) => {
      const chat = chats.find((candidate) => candidate.id === conversationId);
      if (!chat?.peer) return;
      placeCall(conversationId, chat.peer.id, chat.title, chat.peer.avatarPhotoId, video);
    },
    [chats, placeCall],
  );

  const retry = useCallback(() => {
    const attempt = lastAttempt.current;
    clearCall();
    if (!attempt) return;
    placeCall(
      attempt.conversationId,
      attempt.peerId,
      attempt.peerName,
      attempt.peerAvatarId,
      attempt.video,
    );
  }, [clearCall, placeCall]);

  const dismiss = useCallback(() => clearCall(), [clearCall]);

  /**
   * Everything the server has to say about calls.
   *
   * Registered once and reading through refs, so that a re-render never leaves a
   * window where an event has nowhere to land — losing a `call:accepted` would
   * strand the caller on a ringing screen for a call that had been answered.
   */
  useEffect(() => {
    if (!user) return;

    const off = chatSocket.onEvent((event: ServerEvent) => {
      if (event.type === 'call:incoming') {
        // Already on one: the server has told the caller «занято», and this
        // device simply does not ring.
        if (callRef.current) return;
        const incoming: CallState = {
          id: event.callId,
          conversationId: event.conversationId,
          peerId: event.fromUserId,
          peerName: event.fromName,
          peerAvatarId: event.fromAvatarId,
          video: event.video,
          direction: 'incoming',
          // The server's id is already a UUID, which is what lets the socket
          // path and the VoIP push report the same ring once.
          nativeCallId: hasNativeCallKit ? event.callId : null,
          phase: 'incoming',
          muted: false,
          cameraOff: false,
          speakerOn: true,
          facing: cameraFacing(),
          elapsedSeconds: 0,
          connecting: false,
          reconnecting: false,
          peerCameraOff: false,
          endReason: null,
          message: null,
          ringRefused: false,
        };
        callRef.current = incoming;
        setCall(incoming);
        openedAtRef.current = Date.now();
        // One path with the push: the page reports every incoming call, and a
        // UUID the VoIP push already reported is a no-op on the native side.
        if (incoming.nativeCallId) {
          reportIncomingCall(incoming.nativeCallId, event.fromName, event.video);
        }
        // Fetched now rather than on answer, so that picking up is not waiting
        // on a round trip before it can open the microphone.
        void iceForCall().catch(() => {});
        // The cold-start join: this ring may be the one somebody already
        // answered on the lock screen, seconds before this socket existed.
        const held = pendingAnswer.current;
        if (held?.callId === event.callId) {
          pendingAnswer.current = null;
          if (isFreshAnswer(held, Date.now())) answer(event.video, 'callkit');
        }
        return;
      }

      const current = callRef.current;
      if (!current) return;

      if (event.type === 'call:placed') {
        /**
         * The outgoing call has a name now, and that is what makes it
         * cancellable: `hangUp` sends nothing while the id is empty, so before
         * this arrived a caller who changed their mind during the ring closed
         * their own screen and left the other phone ringing out the timer.
         *
         * Matched on the conversation rather than accepted blindly — a reply to
         * an invite this screen has already moved on from must not rename the
         * call that replaced it.
         */
        if (
          current.direction === 'outgoing' &&
          !current.id &&
          current.conversationId === event.conversationId
        ) {
          update({ id: event.callId });
        }
        return;
      }

      if (event.type === 'call:accepted') {
        // The invite had no id to wait on, so this is also where an outgoing
        // call learns which call it turned out to be. The OS keeps knowing it
        // by the locally-minted UUID; only the server's vocabulary changes.
        if (current.nativeCallId) reportCallConnected(current.nativeCallId);
        update({
          id: event.callId,
          phase: 'active',
          connecting: true,
          peerCameraOff: current.video && !event.video,
        });
        void (async () => {
          try {
            const config = await iceForCall();
            const connection = await buildPeer(config, event.callId, current.video);
            // Null is «the call went away while this was being built», which
            // is not a failure to report: nothing is left to offer to.
            await connection?.start();
          } catch (error) {
            noteSetupFailure('offering', error);
            finish('lost', 'failed', callMediaError(error));
            chatSocket.hangup(event.callId);
          }
        })();
        return;
      }

      if (event.type === 'call:cancelled') {
        // Answered on another device of this person's. Not an ended call from
        // this screen's point of view — just one it is no longer part of.
        if (current.id === event.callId) {
          reportEndOnce(current.nativeCallId, 'answered-elsewhere');
          clearCall();
        }
        return;
      }

      if (event.type === 'call:signal') {
        const payload = event.payload as SignalPayload;
        const connection = peer.current;
        if (!connection) {
          // The connection is still being built — see pendingSignals. Held
          // rather than dropped, and an offer or an answer says so out loud:
          // a lost candidate costs a path, a lost offer costs the whole call,
          // and knowing the window is real on this person's phone is the
          // difference between a mystery and a measurement.
          pendingSignals.current.push(payload);
          if (payload.kind === 'offer' || payload.kind === 'answer') {
            noteCall({
              detail: `call/signal/early-${payload.kind}`,
              message: 'signal arrived before the connection existed · queued',
            });
          }
          return;
        }
        void connection.accept(payload).catch((error: unknown) => {
          noteSetupFailure(`signal ${payload.kind}`, error);
        });
        return;
      }

      if (event.type === 'call:ended') {
        // An empty callId is a refusal of an invite that never became a call —
        // busy, or a chat that cannot be called.
        if (event.callId && current.id && event.callId !== current.id) return;
        // Before finish's net, which would call everything 'failed': this is
        // the far side acting, and Recents should say what happened rather
        // than who is to blame. Swift maps the vocabulary onto CallKit's.
        reportEndOnce(
          current.nativeCallId,
          event.reason === 'timeout' || event.reason === 'failed' ? event.reason : 'remote',
        );
        /**
         * A decline nobody could have made.
         *
         * «Отклонён» is the app working — somebody chose — and so files
         * nothing, which left one device's calls dying instantly with no
         * trace anywhere. A ring the far end refused inside two seconds is
         * not a choice: it is something on that phone answering for its
         * owner, and this is the only place that can say so.
         */
        if (
          event.reason === 'declined' &&
          current.direction === 'outgoing' &&
          openedAtRef.current > 0 &&
          Date.now() - openedAtRef.current < 2000
        ) {
          noteCall({
            detail: 'call/declined-instantly',
            message: 'declined before anyone could have answered',
          });
        }
        if (event.reason === 'busy') {
          finish('busy', 'busy');
          return;
        }
        if (event.reason === 'failed' && current.phase !== 'active') {
          finish('lost', 'failed', 'Не удалось соединиться');
          return;
        }
        finish('ended', event.reason);
      }
    });

    return off;
  }, [
    answer,
    buildPeer,
    clearCall,
    finish,
    iceForCall,
    noteCall,
    noteSetupFailure,
    reportEndOnce,
    update,
    user,
  ]);

  /**
   * Actions taken on the system UI: answer and hang-up from the lock screen,
   * CarPlay or AirPods, mute from the call screen iOS draws.
   *
   * Two deliveries of the same events, applied through one reducer. The drain
   * is the one that matters — an answer tapped on a cold start happened
   * before this file existed, and it sits queued in the native half until
   * asked for — and the live listener is for the ordinary foreground case.
   * The overlap is free because applying is idempotent: an answer for the
   * call already active is nothing, an end for a call already gone is
   * nothing, a mute already matching moves nothing.
   */
  /**
   * Whether an action names the call this device is on.
   *
   * Two ids for one call, and which one arrives depends on where the button
   * was: CallKit knows a call by the UUID the report gave it, while a
   * notification's buttons carry the server's id, because that is the only id
   * an FCM payload has ever contained. On Android `nativeCallId` is always null
   * — there is no CallKit to mint one — so a check on it alone compiles, runs,
   * and quietly does nothing at all.
   */
  const namesCurrent = useCallback(
    (current: CallState | null, callId: string): current is CallState => {
      if (!current) return false;
      return current.nativeCallId === callId || current.id === callId;
    },
    [],
  );

  const applyCallKitActions = useCallback(
    (actions: CallKitActions) => {
      for (const callId of actions.ended) {
        if (pendingAnswer.current?.callId === callId) pendingAnswer.current = null;
        const current = callRef.current;
        if (namesCurrent(current, callId)) {
          // Said out loud, because from the far end this is indistinguishable
          // from a person tapping «Отклонить»: a call that ends the instant it
          // is placed, and a caller told they were declined by somebody who
          // never saw the ring. Whatever moved the system UI — a provider
          // reset, a stale call, a lock-screen tap — the row names it as the
          // system's action rather than a choice.
          noteCall({
            detail: 'call/callkit/ended-by-system',
            message: `system ended the call · phase ${current.phase}`,
          });
          if (current.phase === 'incoming') decline('system');
          else hangUp();
        }
      }
      if (actions.answer) {
        const current = callRef.current;
        if (namesCurrent(current, actions.answer.callId) && current.phase === 'incoming') {
          answer(current.video, 'callkit');
        } else if (!current) {
          // No such call yet: the tap outran the socket. Held for the
          // call:incoming that deliverPending is about to send.
          pendingAnswer.current = actions.answer;
        }
      }
      const current = callRef.current;
      if (current?.nativeCallId) {
        const on = actions.muted.get(current.nativeCallId);
        if (on !== undefined && on !== current.muted) toggleMute();
      }
      // A ring the system declined to show — Do Not Disturb and its relatives.
      // Filed, and acted on: with no system ring there is no system screen
      // either, so the page takes the ring back rather than leaving an
      // incoming call with no UI anywhere. See CallState.ringRefused.
      for (const refusal of actions.refusals) {
        noteCall({
          detail: 'call/callkit/ring-refused',
          message: `system declined to ring · ${refusal.reason}`,
        });
        if (callRef.current?.nativeCallId === refusal.callId) update({ ringRefused: true });
      }
    },
    [answer, decline, hangUp, namesCurrent, noteCall, toggleMute, update],
  );

  useEffect(() => {
    if (!user || (!hasNativeCallKit && !hasNativePushCallActions)) return;

    // Two sources, one reducer. CallKit's queue is filled by the system call
    // screen; the push plugin's by the buttons on a notification, which is how
    // Android answers a call at all and how iOS will once its own actions land.
    // Neither knows about the other, so both are drained and folded together —
    // an answer followed by its own decline has to cancel out whichever queue
    // each arrived in.
    const drain = () => {
      void Promise.all([drainCallKitEvents(), drainNativePushCallActions()]).then((batches) => {
        const events = batches.flat();
        if (events.length > 0) applyCallKitActions(reduceCallKitEvents(events, Date.now()));
      });
    };
    // On mount and on every return to visibility — the same shape ChatContext
    // uses for push taps, guarding the same worst case: the action somebody
    // took while JavaScript was not running.
    drain();
    const onVisible = () => {
      if (document.visibilityState === 'visible') drain();
    };
    document.addEventListener('visibilitychange', onVisible);
    const unwatch = watchCallKitActions((event: CallKitEvent) => {
      applyCallKitActions(reduceCallKitEvents([event], Date.now()));
    });
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      unwatch();
    };
  }, [applyCallKitActions, user]);

  /**
   * Rings, for as long as this device is the one being called.
   *
   * On the phone the notification has already made a noise; this is for the app
   * that is open, where an incoming call used to arrive in total silence. The
   * session goes to the playback category first so that the ringtone is audible
   * with the ringer switch off, the way a call is and a notification is not.
   */
  const ringing = call?.phase === 'incoming';

  /** The system rings, and draws the screen, unless it refused to. */
  const systemRinging = hasNativeCallKit && ringing && !call?.ringRefused;

  useEffect(() => {
    // With CallKit the system rings — its own sound, its own screen — and a
    // second ringtone underneath it would be an echo. lib/ringtone.ts stays
    // for the web, for Android, for a shell older than this bundle, and for
    // the call the system refused to ring at all, which is the one case where
    // silence here would mean silence everywhere.
    if (!ringing || systemRinging) return;
    setCallRinging(true);
    const context = ringtoneContext();
    const stop = context ? startRingtone(context) : null;
    return () => {
      stop?.();
      setCallRinging(false);
    };
    // systemRinging as well as ringing, because it changes *during* a ring:
    // the refusal arrives a beat after the report, and that beat is exactly
    // when this has to start making a noise.
  }, [ringing, systemRinging]);

  /**
   * What the phone's audio session has to say while a call is on.
   *
   * A real GSM call interrupting is the one that matters: iOS takes the session
   * away and does **not** give it back, so the far end stays silent afterwards
   * unless somebody claims it again. Route changes are the smaller half — a
   * pair of headphones going in moves the speaker button without a tap.
   *
   * From the tap rather than from the answer, and that is not a widening for
   * its own sake: the caller claims the session in `placeCall`, seconds before
   * anybody picks up, and the plugin announces the route it settled on the
   * moment it settles. Waiting for `active` meant nobody was listening when it
   * did — so on Android, where nothing else re-announces a route that never
   * changes, the *calling* side's route was never written down at all. Six
   * calls in one day had a route filed for whoever answered and nothing for
   * whoever rang, which reads as «проверено» and is half of it. The flag stays
   * true across outgoing → active, so the listener is attached once.
   */
  const active = call?.phase === 'active';
  const claimed = active || call?.phase === 'outgoing';
  const activeVideo = call?.video ?? false;

  useEffect(() => {
    if (!claimed) return;
    /** Whether the route below is the one the call opened on. */
    let opened = false;
    return watchCallAudio({
      onInterruption: (began) => {
        if (!began) startCallAudio(activeVideo);
      },
      onRoute: (speaker, where) => {
        update({ speakerOn: speaker });
        // Filed, not just shown on the button. «Звук совсем тихий» has exactly
        // one cause the loudness figures cannot separate from a quiet
        // microphone — sound coming out of the earpiece, or out of a
        // loudspeaker whose stream is turned down — and this line is the only
        // place either fact is ever written down.
        //
        // The route the call opened on and the route having *moved* are filed
        // apart, and that is the whole of what makes the second one readable.
        // The plugin now watches the route for the length of the call and
        // announces only changes; the reporter throttles by detail for thirty
        // seconds, so a second row a moment later saying «теперь наушник»
        // would be dropped as a repeat of the opening one — which is the row
        // that was worth having. Both still land on the call's own timeline,
        // which is not throttled at all.
        const detail = opened ? 'call/audio/route/moved' : 'call/audio/route';
        opened = true;
        noteCall({ detail, message: `audio out of ${where}` });
      },
    });
  }, [claimed, activeVideo, noteCall, update]);

  /**
   * Keeps the screen awake for the length of a call.
   *
   * Re-acquired on visibility change because the lock is dropped whenever the
   * page is hidden and is not restored on its own — coming back from the app
   * switcher mid-call would otherwise leave the screen free to sleep again.
   *
   * Keyed on whether a call is active rather than on the call, because the call
   * is replaced once a second by the elapsed timer: depending on it released
   * and re-requested the lock every second for the whole conversation.
   */
  useEffect(() => {
    if (!active) return;
    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        sentinel = (await navigator.wakeLock?.request('screen')) ?? null;
        if (cancelled) void sentinel?.release();
      } catch {
        // Unsupported, or refused because the page is not visible. The call is
        // unaffected; the screen may dim.
      }
    };
    void acquire();

    const onVisible = () => {
      if (document.visibilityState === 'visible') void acquire();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void sentinel?.release().catch(() => {});
    };
  }, [active]);

  /**
   * When the call left the screen, and for how long.
   *
   * The one fact «чёрный экран» needs and no statistic carries. An Android skin
   * that considers a backgrounded app to be spying — MIUI and HONOR both do —
   * does not stop the camera when the app goes away: it goes on delivering
   * frames, and every one of them is black. The track stays live, the encoder
   * stays busy, and the sample that should explain it says a full frame rate at
   * twenty kilobits, which reads as a starved network. It also refuses to open
   * a camera at all while the app is away, which is a flip that does nothing.
   *
   * Two lines in the journal beside those numbers turn all of it into one
   * sentence. Keyed on `active` rather than on the call for the same reason the
   * wake lock above is: the call object is replaced once a second by the timer.
   */
  useEffect(() => {
    if (!active) return;
    let leftAt: number | null = null;
    const onVisibility = () => {
      const now = Date.now();
      if (document.visibilityState === 'visible') {
        const away = leftAt === null ? null : Math.round((now - leftAt) / 1000);
        leftAt = null;
        noteCall({
          detail: 'call/page/visible',
          message: `call back on screen${away === null ? '' : ` after ${away}s away`}`,
        });
        return;
      }
      leftAt = now;
      noteCall({ detail: 'call/page/hidden', message: 'call left the screen' });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [active, noteCall]);

  /** Signing out mid-call leaves nothing running. */
  useEffect(() => {
    if (!user && callRef.current) clearCall();
  }, [clearCall, user]);

  /**
   * A call this page never had, from a page that is gone. See
   * closeOrphanedNativeCall — mounting is the one moment at which a live
   * native call can only be somebody else's leftover.
   */
  useEffect(() => {
    closeOrphanedNativeCall();
  }, []);

  const value = useMemo<CallContextValue>(
    () => ({
      call,
      localStream,
      remoteStream,
      minimized,
      hasRelay,
      nativeCall: hasNativeCallPeer,
      nativeCallVideo: nativeVideo,
      systemRinging,
      placeCall,
      callBack,
      answer,
      decline,
      hangUp,
      toggleMute,
      toggleCamera,
      toggleSpeaker,
      flip,
      setMinimized,
      retry,
      dismiss,
      noteCall,
    }),
    [
      answer,
      call,
      callBack,
      decline,
      dismiss,
      flip,
      hangUp,
      noteCall,
      hasRelay,
      localStream,
      minimized,
      nativeVideo,
      placeCall,
      remoteStream,
      retry,
      systemRinging,
      toggleCamera,
      toggleMute,
      toggleSpeaker,
    ],
  );

  return (
    <CallCtx.Provider value={value}>
      {children}
      {/* Deliberately a sibling of the whole app rather than of the overlay:
          the far end has to stay audible while the call is minimised and on a
          voice call, which is where the overlay renders nothing. See
          RemoteAudio, which is entirely about that sentence. */}
      <RemoteAudio stream={remoteStream} onDiagnostic={noteCall} />
    </CallCtx.Provider>
  );
}
